<template>
    <div>
        <loading v-if="loading" size="small"></loading>
        <div v-else>
            <ul class="space-y-8 mb-16">
                <li>
                    <div class="flex items-center">
                        <checkbox class="w-full" v-model="filter.new">
                            <div class="flex items-center">
                                <progress-icon class="mr-8"></progress-icon>
                                <div>
                                    <span class="capitalize">{{ $t('general.new') }}</span>
                                    -
                                    <span class="font-normal text-primary-80">{{ newCount }} {{ $t('applications.title') }}</span>
                                </div>
                            </div>
                        </checkbox>
                    </div>
                </li>
                <li>
                    <div class="flex items-center">
                        <checkbox class="w-full" v-model="filter.hired">
                            <div class="flex items-center">
                                <progress-icon :progress="100" class="mr-8"></progress-icon>
                                <div>
                                    <span class="capitalize">{{ $t('analytics.hired') }}</span>
                                    -
                                    <span class="font-normal text-primary-80">{{ hiredCount }} {{ $t('applications.title') }}</span>
                                </div>
                            </div>
                        </checkbox>
                    </div>
                </li>
                <li class="pb-4">
                    <div class="flex items-center">
                        <accordion-item :title="$t('applications.rejected')"
                                        icon="rejected"
                                        icon-color="red-600"
                                        :border="false"
                                        chevron-alignment="left"
                                        padding="none"
                                        content-classes="ps-12"
                        >
                            <template #header>
                                <div class="flex items-center" role="button">
                                    <checkbox :checked="hasSelectedItems"
                                              class="pointer-events-none mr-8"
                                              />
                                    <progress-icon class="mr-8" rejected/>
                                    <div>
                                        <span class="capitalize">
                                            {{ $t('applications.rejected') }}
                                        </span>
                                        -
                                        <span class="font-normal text-primary-80">
                                            {{ rejectedCount + ' ' + $t('applications.title') }}
                                        </span>
                                    </div>
                                </div>
                            </template>
                            <template #content>
                                <ul class="space-y-8">
                                    <li class="pb-6">
                                        <checkbox class="w-full"
                                                  value="all" @change="setSelection('all');"
                                                  :checked="filter.rejected.includes('all')">
                                            <div>
                                                <span class="capitalize">
                                                    {{ $t('jobs.all_rejected_status') }}
                                                </span>
                                                -
                                                <span class="font-normal text-primary-80">
                                                    {{ rejectedCount + ' ' + $t('applications.title') }}
                                                </span>
                                            </div>
                                        </checkbox>
                                    </li>
                                    <li v-for="reason in reasons">
                                        <checkbox class="w-full" :value="reason.label"
                                                  @change="setSelection(reason);"
                                                  :checked="filter.rejected.includes('all') || filter.rejected.includes(reason.label)"
                                                  v-if="!reason.no_title"
                                        >
                                            <span>
                                                <span class="capitalize">
                                                    {{ reason.label }}
                                                </span>
                                                -
                                                <span class="font-normal text-primary-80">
                                                    {{ reason.application_count + ' ' + $t('applications.title') }}
                                                </span>
                                            </span>
                                        </checkbox>
                                        <tooltip :content="$t('applications.reject_reason.no_title')"
                                                 wrapper-class="flex-none"
                                                 tooltip-class="max-w-full text-wrap"
                                                 v-else>
                                            <checkbox class="w-full" :value="reason.label"
                                                      @change="setSelection(reason);"
                                                      :checked="filter.rejected.includes('all') || filter.rejected.includes(reason.label)"
                                            >
                                            <span>
                                                <span class="italic capitalize">
                                                    {{ reason.label }}
                                                </span>
                                                -
                                                <span class="font-normal text-primary-80">
                                                    {{ reason.application_count + ' ' + $t('applications.title') }}
                                                </span>
                                            </span>
                                            </checkbox>
                                        </tooltip>
                                    </li>
                                    <li v-if="unusedReasons.length" class="flex items-center relative py-12">
                                        <div class="w-3/5 border-b-2 border-primary-20"></div>
                                        <span class="text-primary-40 absolute left-0 ms-5 bg-white px-3">
                                            {{
                                                $tc('reject_reasons.unused', unusedReasons.length, {count: unusedReasons.length})
                                            }}
                                        </span>
                                    </li>
                                    <li v-if="unusedReasons.length" v-for="reason in unusedReasons">
                                        <checkbox class="w-full" :value="reason.label"
                                                  :disabled="true">
                                            <span>
                                                <span class="capitalize text-primary-40">
                                                    {{ reason.label }}
                                                </span>
                                                -
                                                <span class="font-normal text-primary-40">
                                                    {{ 0 + ' ' + $t('applications.title') }}
                                                </span>
                                            </span>
                                        </checkbox>
                                    </li>
                                </ul>
                            </template>
                        </accordion-item>
                    </div>
                </li>
                <hr class="w-1/3 pb-4">
                <li>
                    <div class="flex items-center">
                        <checkbox class="w-full" v-model="filter.stale">
                            <div class="flex items-center">
                                <svg-icon class="text-semantic-warning w-20 h-20 mr-8" type="checklist"></svg-icon>
                                <div>
                                    <span class="capitalize">{{ $t('general.stale') }}</span>
                                    -
                                    <span class="font-normal text-primary-80">
                                        {{ staleCount }} {{ $t('applications.title') }}
                                    </span>
                                </div>
                            </div>
                        </checkbox>
                    </div>
                </li>
                <li>
                    <div class="flex items-center">
                        <checkbox class="w-full" v-model="filter.interview">
                            <div class="flex items-center">
                                <svg-icon class="text-secondary-40 w-20 h-20 mr-8" type="check-circle"></svg-icon>
                                <div>
                                    <span class="capitalize">{{ $t('applications.planned_for_interview') }}</span>
                                    -
                                    <span class="font-normal text-primary-80">{{ interviewApplicationCount }} {{ $t('applications.title') }}</span>
                                </div>
                            </div>
                        </checkbox>
                    </div>
                </li>
                <li>
                    <div class="flex items-center">
                        <checkbox class="w-full" v-model="filter.unconfirmedInterview">
                            <div class="flex items-center">
                                <svg-icon class="text-primary-40 w-20 h-20 mr-8" type="pending"></svg-icon>
                                <div>
                                    <span class="capitalize">{{
                                            $t('applications.unplanned_for_interview')
                                        }}</span>
                                    -
                                    <span class="font-normal text-primary-80">{{ unconfirmedInterviewApplicationCount }} {{ $t('applications.title') }}</span>
                                </div>
                            </div>
                        </checkbox>
                    </div>
                </li>
            </ul>
            <div class="pt-8 border-t border-primary-5">
                <h3 class="font-semibold text-body mb-8">{{ $t('applications.filters.specific_stages') }}</h3>
            </div>
            <simple-input v-model="search" :placeholder="$t('general.search') + '…'" class="mb-8"></simple-input>
            <div class="max-h-320 overflow-y-auto">
                <ul class="space-y-8">
                    <li
                        v-for="stage in stageList"
                        :key="'stage-' + stage.item.name"
                        :class="{ hidden: stage.score && stage.score > 0.5}"
                    >
                        <div class="flex items-center">
                            <checkbox
                                class="w-full"
                                v-model="filter.stages"
                                :value="stage.item.name + ':' + stage.item.stages"
                            >
                                <span>{{ stage.item.name }}</span>
                                -
                                <span class="font-normal text-primary-80">{{ stage.item.application_count }} {{ $t('applications.title') }}</span>
                            </checkbox>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
import ProgressIcon from '../ui/ProgressIcon.vue';
import * as stages from '../../services/StageService';
import * as ApplicationService from '../../services/ApplicationService';
import {mapState} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useAuthenticationStore} from "../../state/modules/authentication";
import {useDashboardStore} from "../../state/modules/dashboard";
import Loading from '../ui/Loading.vue';
import Checkbox from '../forms/Checkbox.vue';
import Input from '../forms/Input.vue';
import Fuse from 'fuse.js';
import SvgIcon from '../ui/SvgIcon.vue';
import RejectReason from '../../models/RejectReason';
import {normalizeJson} from '../../../shared/helpers';
import AccordionItem from "../ui/AccordionItem.vue";
import Tooltip from "../navigation/Tooltip.vue";

export default {
    components: {
        Tooltip,
        AccordionItem,
        SvgIcon,
        'progress-icon': ProgressIcon,
        'loading': Loading,
        'checkbox': Checkbox,
        'simple-input': Input,
    },
    props: {
        filter: Object,
    },
    data: function () {
        return {
            fuse: null,
            stages: [],
            rejectedCount: 0,
            hiredCount: 0,
            newCount: 0,
            staleCount: 0,
            search: '',
            loading: true,
            selectedStages: [],
            selectedReasons: [],
            allRejected: false,
            unusedReasons: [],
        };
    },
    computed: {
        ...mapState(useAuthenticationStore, ['currentCompanyId']),
        ...mapState(useDashboardStore, ['interviewApplicationCount', 'unconfirmedInterviewApplicationCount']),
        stageList() {
            return this.search.trim() === ''
                ? this.unfilteredStages
                : this.filteredStages;
        },
        filteredStages() {
            return this.fuse ? this.fuse.search(this.search.trim()) : [];
        },
        unfilteredStages() {
            return this.stages.map(stage => ({item: stage}));
        },
        reasons() {
            return useRepo(RejectReason).orderBy('order').get().map(item => ({
                label: item.title,
                value: item.id,
                application_count: item.application_count,
                no_title: item.no_title,
            }));
        },
        hasSelectedItems() {
            return this.filter.rejected.length > 0;
        },
    },
    methods: {
        setSelection(reason) {
            if (reason !== 'all' && this.filter.rejected.includes('all')) {
                this.filter.rejected = this.reasons.filter(item => item.label !== reason.label).map(item => item.label);
                return;
            }
            if (reason === 'all') {
                if (this.filter.rejected.includes('all')) {
                    this.filter.rejected = [];
                } else {
                    this.filter.rejected = ['all'];
                }
                return;
            }

            if (this.filter.rejected.includes(reason.label)) {
                this.filter.rejected.splice(this.filter.rejected.indexOf(reason.label), 1);
            } else {
                if (this.filter.rejected.length + 1 === this.reasons.length) this.filter.rejected = ["all"];
                else this.filter.rejected.push(reason.label);
            }
        },
    },
    mounted() {
        Promise.all([
            stages.index(this.currentCompanyId, false).then(result => {
                this.rejectedCount = result.rejectedCount;
                this.staleCount = result.staleCount;
                this.newCount = result.newCount;
                this.hiredCount = result.hiredCount;
                this.stages = result.stages.filter(stage => stage.order > 0 && !stage.onboarding);
                this.fuse = new Fuse(this.stages, {
                    keys: ['name'],
                    threshold: 1,
                    includeScore: true,
                });
            }),
            ApplicationService.rejectReasons().then(({data}) => {
                // delete reject reasons that are temp
                useRepo(RejectReason).flush();
                useRepo(RejectReason).save(normalizeJson(data.reject_reasons.data).map(reasons => {
                    return { ...reasons, temp: true };
                }));

                // get unused reject reasons
                this.unusedReasons = normalizeJson(data.unused_reject_reasons.data).map(item => ({
                    label: item.title,
                    value: item.id,
                    application_count: item.application_count,
                }));
            }),
        ]).then(() => {
            this.loading = false;
        });
    },
    beforeUnmount() {
        useRepo(RejectReason).where('temp', true).delete();
    },
};
</script>
